import axios, { type AxiosInstance } from 'axios';

const sessionPath = '/v2/auth/session';

function record(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Refresh cached membership after an authorized firm's routing changes.
 * Never replay the denied request: it may contain a write for the old firm.
 */
export function installMembershipRecovery(
  api: AxiosInstance,
  reload: () => void = () => window.location.reload(),
): void {
  let refresh: Promise<void> | undefined;
  let reloading = false;

  api.interceptors.response.use(undefined, async (error: unknown) => {
    if (!axios.isAxiosError(error) || error.response?.status !== 403 || reloading) {
      throw error;
    }

    try {
      const stored = localStorage.getItem('user');
      const token = localStorage.getItem('access_token');
      const cached: unknown = JSON.parse(stored ?? 'null');
      const user: unknown = record(cached) ? JSON.parse(String(cached.value)) : null;
      if (
        !record(cached) ||
        typeof cached.expiry !== 'number' ||
        cached.expiry <= Date.now() ||
        !record(user) ||
        typeof user.id !== 'string' ||
        typeof user.client_id !== 'string' ||
        !error.config?.url?.startsWith(`/clients/${user.client_id}/`) ||
        !token
      ) {
        throw error;
      }

      if (!refresh) {
        refresh = (async () => {
          const response: unknown = await api.get(sessionPath);
          const data = record(response) ? response.data : null;
          const current = record(data) ? data.user : null;
          if (
            !record(current) ||
            current.id !== user.id ||
            typeof current.client_id !== 'string' ||
            !current.client_id ||
            current.client_id === user.client_id ||
            localStorage.getItem('user') !== stored ||
            localStorage.getItem('access_token') !== token ||
            cached.expiry <= Date.now()
          ) {
            return;
          }

          const context = record(data) ? data.firm_data_context : null;
          localStorage.setItem(
            'user',
            JSON.stringify({
              ...cached,
              value: JSON.stringify({ ...current, firm_data_context: context }),
            }),
          );
          if (context) {
            localStorage.setItem(
              'firm_data_context',
              JSON.stringify({ value: JSON.stringify(context), expiry: cached.expiry }),
            );
          } else {
            localStorage.removeItem('firm_data_context');
          }
          reloading = true;
          reload();
        })().finally(() => {
          refresh = undefined;
        });
      }
      await refresh;
    } catch {
      // An unavailable session endpoint or an unchanged denial remains a denial.
    }
    throw error;
  });
}
